import { useState, useEffect } from "react";
import { saCellphoneFormRule } from "../../../utils/systemValidation";
import {
  Button,
  Input,
  InputNumber,
  Form,
  Select,
  Space,
  Row,
  Col,
  Card,
  DatePicker,
  Skeleton,
  message,
  Modal,
} from "antd";
import {
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { SelectProps } from "antd/lib/select";

import {
  addNupayDebiCheckPaymentApi,
  addSecondaryPaymentApi,
  editDebiCheckPaymentApi,
  removeNupayDebiCheckPaymentApi,
} from "../../../services/Payment/PaymentApis";
import { getMemberApi } from "../../../services/Member/MemberApis";
import { mandateCheck } from "../../../utils/debiCheckHelper";
import DebiCheckMaintenanceModal from "./DebiCheckMaintenanceModal";

interface StepProps {
  buttonLoading: boolean;
  gotoStep: Function;
  form: any;
  update: boolean;
  submit: Function;
  memberId: any;
}

interface CardData {
  id: number;
  Type: string;
  Bank: string;
  AccountNumber: string;
  AccountType: string;
  BranchCode: string;
  StartDate: string;
  FirstCollectionDate?: string | null;
  FirstCollectionAmount?: string | null;
  MandateId?: string | number | null;
  MandateInstructionId?: string | number | null;
  PaymentDate: string;
  Cellphone: string;
  Email: string;
  IdNumber: string;
  Name: string;
  Amount: string;
  PaymentFrequency: string;
  MandateStatus: string;
  MandateInstructionStatus: string;
  MandateAccepted: boolean;
  MandateProcessed: boolean;
}

interface FormData {
  Type: string;
  Bank: string;
  AccountNumber: string;
  AccountType: string;
  BranchCode: string;
  PaymentDate: string;
  Cellphone: string;
  Email: string;
  IdNumber: string;
  Name: string;
  Amount: string;
  PaymentFrequency: string;
}

// Add Nupay Ids to value
const bank: SelectProps["options"] = [
  {
    label: "SBSA",
    value: "1",
  },
  {
    label: "Access",
    value: "Access",
  },
  {
    label: "ABSA",
    value: "16",
  },
  {
    label: "African Bank",
    value: "7",
  },
  {
    label: "Bidvest Bank",
    value: "44",
  },
  {
    label: "Capitec",
    value: "10",
  },
  {
    label: "FinBond",
    value: "55",
  },
  {
    label: "FNB",
    value: "3",
  },
  {
    label: "Nedbank",
    value: "2",
  },
  {
    label: "TymeBank",
    value: "61",
  },
];

const payment: SelectProps["options"] = [
  {
    name: "Type",
    label: "Debi-Check",
    value: "DebiCheck",
  },
  {
    name: "Type",
    label: "DebitOrder",
    value: "DebitOrder",
  },
];

const account: SelectProps["options"] = [
  {
    name: "AccountType",
    label: "Savings",
    value: "Savings",
  },
  {
    name: "AccountType",
    label: "Current",
    value: "Current",
  },
];

const frequencyOpt: SelectProps["options"] = [
  {
    name: "Weekly",
    label: "Weekly",
    value: "Weekly",
  },
  {
    name: "Monthly",
    label: "Monthly",
    value: "Monthly",
  },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 14 },
};

const NupayPaymentInfo: React.FC<StepProps> = ({
  buttonLoading,
  gotoStep,
  form,
  update,
  submit,
  memberId,
}) => {
  const [card, setCard] = useState<CardData[]>([]);
  const [formData, setFormData] = useState<FormData>({
    Name: "",
    Cellphone: "",
    Email: "",
    IdNumber: "",
    Type: "DebiCheck",
    Bank: "",
    AccountNumber: "",
    AccountType: "",
    BranchCode: "",
    PaymentDate: "",
    Amount: "",
    PaymentFrequency: "",
  });
  // const [paymentId, setPaymentId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [processed, setProcessed] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(update);
  const [member, setMember] = useState<any>();
  const [maintenanceModalVisible, setMaintenanceModalVisible] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [payAtPayment, setPayAtPayment] = useState<any>(null);

  // Left form is read-only while a mandate exists; edit via Maintain Mandate
  const fieldsReadOnly = disabled || card.length > 0;

  const syncFormFromMandate = (
    mandate: CardData | Record<string, any>,
    memberData?: any
  ) => {
    const sourceMember = memberData ?? member;
    const nextValues = {
      Name: mandate?.Name ?? sourceMember?.Name ?? form.getFieldValue("Name"),
      Surname:
        sourceMember?.Surname ?? form.getFieldValue("Surname"),
      Cellphone:
        mandate?.Cellphone ??
        sourceMember?.Cellphone ??
        form.getFieldValue("Cellphone"),
      Email:
        mandate?.Email ?? sourceMember?.Email ?? form.getFieldValue("Email"),
      IdNumber:
        mandate?.IdNumber ??
        sourceMember?.IdNumber ??
        form.getFieldValue("IdNumber"),
      AccountNumber:
        mandate?.AccountNumber ?? form.getFieldValue("AccountNumber"),
      Bank: mandate?.Bank ?? form.getFieldValue("Bank"),
      AccountType: mandate?.AccountType ?? form.getFieldValue("AccountType"),
      BranchCode: mandate?.BranchCode ?? form.getFieldValue("BranchCode"),
      Amount: mandate?.Amount ?? form.getFieldValue("Amount"),
      PaymentDate: mandate?.PaymentDate ?? form.getFieldValue("PaymentDate"),
      Type: "DebiCheck",
      PaymentFrequency: mandate?.PaymentFrequency || "Monthly",
    };

    form.setFieldsValue(nextValues);
    setFormData((prevData) => ({
      ...prevData,
      ...nextValues,
    }));
  };

  useEffect(() => {
    // Get user role from localStorage or context
    const storedUser = localStorage.getItem("CurrentUser");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log("User data:", user); // Debug log
        const role = user?.Role?.Name || "";
        console.log("Detected role:", role); // Debug log
        setUserRole(role);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleInputNumberChange = (value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      PaymentDate: value,
    }));
  };

  const handleSelectChange = (e: any, id: string) => {
    switch (id) {
      case "Bank": {
        setFormData((prevData) => ({
          ...prevData,
          Bank: e,
        }));
        break;
      }
      case "AccountType": {
        setFormData((prevData) => ({
          ...prevData,
          AccountType: e,
        }));
        break;
      }
      case "PaymentFrequency": {
        setFormData((prevData) => ({
          ...prevData,
          PaymentFrequency: e,
        }));
        break;
      }
    }
  };

  const handleDateChange = (id: string, value: any) => {
    switch (id) {
      case "StartDate": {
        setFormData((prevData) => ({
          ...prevData,
          StartDate: value.format("YYYY-MM-DD"),
        }));
        break;
      }
    }
  };

  const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

  const resolveAmount = () => {
    const raw =
      form.getFieldValue("Amount") ??
      formData.Amount ??
      (member as any)?.TotalPremium;
    const amount = Number(raw);
    return Number.isFinite(amount) && amount > 0 ? amount : raw;
  };

  const addMandate = async () => {
    setLoading(true);

    const amount = resolveAmount();
    if (amount === undefined || amount === null || amount === "" || !(Number(amount) > 0)) {
      setLoading(false);
      message.error(
        "Amount is required and must be a valid positive number. Mandate could not be captured, please review information and try again."
      );
      return;
    }

    const payload = {
      ...formData,
      ...form.getFieldsValue(),
      Amount: Number(amount),
      Type: "DebiCheck",
      PaymentFrequency: formData.PaymentFrequency || "Monthly",
    };

    // Call mandate instruction
    const paymentApiResponse = await addNupayDebiCheckPaymentApi(
      memberId,
      payload
    );

    if (!paymentApiResponse.success) {
      setLoading(false);
      message.error(
        `${paymentApiResponse.message}. Mandate could not be captured, please review information and try again.`
      );
      throw new Error("Adding payment details failed.");
    }

    // If successful create card and set Mandate to Pending
    const newCard = {
      id: card.length + 1,
      ...paymentApiResponse.data,
    };

    setCard((prevCard) => [...prevCard, newCard]);

    const memberApiResponse = await getMemberApi(String(memberId));

    if (!memberApiResponse.success) {
      setLoading(false);
      message.error("Fetching member details failed.");
      throw new Error("Fetching member details failed.");
    }

    const paymentTypeId = memberApiResponse.data.PaymentType.id;

    // Run deibiCheckHelper Function
    const mandateResponse = await mandateCheck(
      `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/payment/${paymentTypeId}`
    );

    // If Function returns true update mandate status
    if (mandateResponse) {
      setLoading(false);
      setProcessed(true);
      message.success("Successfully loaded mandate.");
      // Continue checking for a response in the background
    } else {
      message.info("Mandate still being processed.");
      setLoading(false);

      // Removed Background check while integrating new API's
      // while (!processed) {
      //   const { data } = await getPaymentApi(paymentTypeId);
      //   if (data.MandateProcessed) {
      //     setProcessed(true);
      //   } else {
      //     await delay(10000);
      //   }
      // }
    }

    setFormData({
      Type: "",
      Bank: "",
      AccountNumber: "",
      AccountType: "",
      BranchCode: "",
      PaymentDate: "",
      Cellphone: "",
      Email: "",
      IdNumber: "",
      Name: "",
      Amount: "",
      PaymentFrequency: "",
    });
  };

  const addSecondaryPaymentMethod = async () => {
    setLoading(true);

    try {
      const response = await addSecondaryPaymentApi(String(memberId));

      if (!response?.success) {
        setLoading(false);
        message.error(
          response?.message ||
            "Could not add second payment method. Please try again."
        );
        return;
      }

      setPayAtPayment(response.data);
      setLoading(false);
      message.success(
        response.message || "Pay@ added as second payment method."
      );
    } catch (error) {
      setLoading(false);
      message.error("Could not add second payment method. Please try again.");
    }
  };

  const updateMandate = async () => {
    // setLoading(true);

    const amount = resolveAmount();
    if (amount === undefined || amount === null || amount === "" || !(Number(amount) > 0)) {
      setLoading(false);
      message.error(
        "Amount is required and must be a valid positive number. Mandate could not be captured, please review information and try again."
      );
      return;
    }

    const payload = {
      ...formData,
      ...form.getFieldsValue(),
      Amount: Number(amount),
      Type: "DebiCheck",
      PaymentFrequency: formData.PaymentFrequency || "Monthly",
    };

    // // Call mandate instruction
    const paymentApiResponse = await editDebiCheckPaymentApi(
      memberId,
      payload
    );

    if (!paymentApiResponse.success) {
      setLoading(false);
      message.error(
        "Mandate could not be captured, please review information and try again."
      );
      throw new Error("Adding payment details failed.");
    }

    // // If successful create card and set Mandate to Pending
    // const newCard = {
    //   id: card.length + 1,
    //   ...paymentApiResponse.data,
    // };

    // setCard((prevCard) => [...prevCard, newCard]);

    const memberApiResponse = await getMemberApi(String(memberId));

    if (!memberApiResponse.success) {
      setLoading(false);
      message.error("Fetching member details failed.");
      throw new Error("Fetching member details failed.");
    }

    const paymentTypeId = memberApiResponse.data.PaymentType.id;

    // // Run deibiCheckHelper Function
    const mandateResponse = await mandateCheck(
      `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_VERSION}/payment/${paymentTypeId}`
    );

    // // If Function returns true update mandate status
    if (mandateResponse) {
      setLoading(false);
      setProcessed(true);
      message.success("Successfully updated mandate.");
      // Continue checking for a response in the background
    } else {
      message.info("Mandate still being processed.");
      setLoading(false);
    }
    //   // Removed Background check while integrating new API's
    //   // while (!processed) {
    //   //   const { data } = await getPaymentApi(paymentTypeId);
    //   //   if (data.MandateProcessed) {
    //   //     setProcessed(true);
    //   //   } else {
    //   //     await delay(10000);
    //   //   }
    //   // }
    // }
  };

  const editMandate = async (card: CardData) => {
    setDisabled((prev: any) => !prev);
  };

  const removeMandate = async (_card: CardData) => {
    Modal.confirm({
      title: "Cancel NuPay DebiCheck mandate?",
      content:
        "This cancels the mandate with NuPay and removes it from the member. To collect again, re-initiate a new DebiCheck and have the member accept it.",
      okText: "Yes, cancel mandate",
      okType: "danger",
      cancelText: "Back",
      onOk: async () => {
        setLoading(true);
        const paymentApiResponse = await removeNupayDebiCheckPaymentApi(
          memberId
        );

        if (!paymentApiResponse.success) {
          setLoading(false);
          const errMsg = String(paymentApiResponse.message || "");
          // Timed-out cancel may have finished server-side and already removed the mandate.
          if (/already cancelled|no active.*mandate/i.test(errMsg)) {
            setCard([]);
            message.success("Mandate already cancelled. You can create a new DebiCheck.");
            return;
          }
          message.error(
            errMsg || "Mandate could not be cancelled, please try again."
          );
          return;
        }

        setCard([]);
        setLoading(false);
        message.success("Mandate cancelled successfully.");
      },
    });
  };

  useEffect(() => {
    getMemberApi(String(memberId))
      .then((res: any) => {
        const primary = res.data.PaymentType;
        const secondary = res.data.SecondaryPaymentType;
        const debiCheck =
          primary?.Type === "DebiCheck"
            ? primary
            : secondary?.Type === "DebiCheck"
            ? secondary
            : null;
        const payAt =
          primary?.Type === "PayAt"
            ? primary
            : secondary?.Type === "PayAt"
            ? secondary
            : null;

        setMember(res.data);
        setPayAtPayment(payAt || null);

        if (debiCheck != null) {
          setCard(() => [debiCheck]);
          // Keep left form in sync with the active mandate
          syncFormFromMandate(debiCheck, res.data);
        } else {
          const initialFormData = form.getFieldsValue();
          form.setFieldsValue({
            Amount: res.data.TotalPremium,
            Type: "DebiCheck",
            PaymentFrequency: "Monthly",
          });
          setFormData((prevData) => ({
            ...prevData,
            ...initialFormData,
            Amount: res.data.TotalPremium,
            Type: "DebiCheck",
            PaymentFrequency: "Monthly",
          }));
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const content = (
    <>
      <Row>
        <Col span={16}>
          <Form.Item
            name="Name"
            label="Name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              placeholder="Name"
              id="Name"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Surname"
            label="Surname"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              placeholder="Surname"
              id="Surname"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Cellphone"
            label="Cellphone"
            rules={[saCellphoneFormRule]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              id="Cellphone"
              placeholder="Cellphone"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Email"
            label="Email"
            // rules={[
            //   {
            //     required: true,
            //   },
            // ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              id="Email"
              placeholder="Email"
              onChange={handleInputChange}
            />
          </Form.Item>

          <Form.Item
            name="IdNumber"
            label="ID Number"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              placeholder="ID Number"
              id="IdNumber"
              onChange={handleInputChange}
            />
          </Form.Item>
          {/* <Form.Item
            name="Type"
            label="Payment Type"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={fieldsReadOnly}
              id="Type"
              options={payment}
              size="large"
              style={{ width: "100%" }}
              placeholder="Payment Type"
              // onChange={(e) => handleSelectChange(e)}
            />
          </Form.Item> */}
          <Form.Item
            name="AccountNumber"
            label="Account Number"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              id="AccountNumber"
              placeholder="Account Number"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Bank"
            label="Bank"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={fieldsReadOnly}
              id="Bank"
              options={bank}
              size="large"
              style={{ width: "100%" }}
              placeholder="Bank"
              onChange={(e) => handleSelectChange(e, "Bank")}
            />
          </Form.Item>
          <Form.Item
            name="AccountType"
            label="Account Type"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={fieldsReadOnly}
              id="AccountType"
              onChange={(e) => handleSelectChange(e, "AccountType")}
              options={account}
              size="large"
              style={{ width: "100%" }}
              placeholder="Account Type"
            />
          </Form.Item>
          <Form.Item
            name="BranchCode"
            label="Branch Code"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              id="BranchCode"
              placeholder="Branch Code"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="Amount"
            label="Payment Amount"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled={fieldsReadOnly}
              size="large"
              id="Amount"
              placeholder="Payment Amount"
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item
            name="PaymentDate"
            label="Payment Date"
            initialValue={formData.PaymentDate}
            extra="Day of month (1–31). If the month is shorter, collection falls on the last day of that month."
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber
              disabled={fieldsReadOnly}
              size="large"
              min={1}
              max={31}
              onChange={handleInputNumberChange}
            />
          </Form.Item>

          {/* <Form.Item
            name="PaymentFrequency"
            label="Payment Frequency"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              disabled={true}
              options={frequencyOpt}
              style={{ width: "100%" }}
              size="large"
              placeholder="Select Payment Frequency"
              onChange={(e) => handleSelectChange(e, "PaymentFrequency")}
            />
          </Form.Item> */}
          {update ? (
            <Form.Item {...tailLayout}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  disabled={disabled}
                  loading={loading}
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  onClick={updateMandate}
                >
                  <span>Update</span>
                </Button>
              </Space>
            </Form.Item>
          ) : (
            <Form.Item {...tailLayout}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  disabled={card.length > 0 && !!payAtPayment}
                  loading={loading}
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  onClick={
                    card.length > 0 ? addSecondaryPaymentMethod : addMandate
                  }
                >
                  <span>
                    {card.length > 0 ? "Add 2nd Payment Method" : "Add"}
                  </span>
                </Button>
              </Space>
              {card.length > 0 && payAtPayment && (
                <span style={{ color: "#8c8c8c" }}>
                  Second payment method already added.
                </span>
              )}
            </Form.Item>
          )}

          <Form.Item {...tailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                loading={buttonLoading}
                style={{ marginBottom: 10 }}
                block
                size="large"
                onClick={() => {
                  submit().then((result: any) => {
                    if (result) {
                      gotoStep(2);
                    }
                  });
                }}
              >
                Okay
              </Button>
            </Space>
          </Form.Item>
        </Col>

        <Col span={8}>
          <h3 style={{ marginBottom: "16px" }}>
            Current Debi-Check/DebitOrder Mandate
          </h3>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!loading ? (
              card.map((card) => (
                <Card
                  key={card && card.id}
                  title={
                    <div>
                      {card && (card.MandateAccepted || card.MandateInstructionStatus === "Accepted") ? (
                        <>
                          <span style={{ color: "Green" }}>
                            Mandate Accepted -{" "}
                          </span>
                          <CheckCircleOutlined style={{ color: "Green" }} />
                        </>
                      ) : card &&
                        (["rejected", "failed", "error", "declined", "cancelled", "canceled"].includes(
                          String(card.MandateStatus ?? "").toLowerCase()
                        ) ||
                          ["rejected", "failed", "error", "declined"].includes(
                            String(card.MandateInstructionStatus ?? "").toLowerCase()
                          ) ||
                          (card.MandateProcessed === true &&
                            !card.MandateAccepted &&
                            String(card.MandateInstructionStatus ?? "").toLowerCase() !==
                              "accepted")) ? (
                        <>
                          <span style={{ color: "Red" }}>
                            Mandate{" "}
                            {card.MandateStatus ||
                              card.MandateInstructionStatus ||
                              "Rejected"}{" "}
                            -{" "}
                          </span>
                          <StopOutlined style={{ color: "Red" }} />
                        </>
                      ) : card && card.MandateProcessed === false ? (
                        <>
                          <span style={{ color: "Blue" }}>
                            Mandate Pending -{" "}
                          </span>
                          <ClockCircleOutlined style={{ color: "Blue" }} />
                        </>
                      ) : card &&
                        card.MandateProcessed === true &&
                        card.MandateStatus !== "" ? (
                        <>
                          <span style={{ color: "Blue" }}>
                            Mandate {card.MandateStatus} -{" "}
                          </span>
                          <ClockCircleOutlined style={{ color: "Blue" }} />
                        </>
                      ) : (
                        <>
                          <span style={{ color: "Red" }}>
                            Mandate Rejected -{" "}
                          </span>
                          <StopOutlined style={{ color: "Red" }} />
                        </>
                      )}
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <p>
                      Mandate ID -{" "}
                      <strong>
                        {card?.MandateInstructionId != null &&
                        card.MandateInstructionId !== ""
                          ? card.MandateInstructionId
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Mandate Instruction Status -{" "}
                      <strong>{card && card.MandateInstructionStatus}</strong>
                    </p>
                    <p>
                      First collection date -{" "}
                      <strong>
                        {card?.FirstCollectionDate
                          ? card.FirstCollectionDate
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      First collection amount -{" "}
                      <strong>
                        {card?.FirstCollectionAmount != null &&
                        card.FirstCollectionAmount !== ""
                          ? `R ${card.FirstCollectionAmount}`
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Instalment date -{" "}
                      <strong>{card?.StartDate || "N/A"}</strong>
                    </p>
                    <p>
                      Instalment amount -{" "}
                      <strong>
                        {card?.Amount != null && card.Amount !== ""
                          ? `R ${card.Amount}`
                          : "N/A"}
                      </strong>
                    </p>
                    <p>
                      Payment Date - <strong>{card && card.PaymentDate}</strong>{" "}
                    </p>
                  </div>

                  {card && (
                    <>
                      <Button
                        type="default"
                        icon={<ToolOutlined />}
                        onClick={() => setMaintenanceModalVisible(true)}
                        style={{ marginRight: 8 }}
                      >
                        Maintain Mandate
                      </Button>
                      <Button
                        type="primary"
                        danger
                        loading={loading}
                        onClick={() => removeMandate(card)}
                      >
                        Cancel Mandate
                      </Button>
                    </>
                  )}

                  {/* <Button
                    type="primary"
                    onClick={() => editMandate(card)}
                    style={{ marginRight: 8 }}
                  >
                    Edit
                  </Button> */}
                </Card>
              ))
            ) : (
              <Card>
                <Skeleton active />
              </Card>
            )}
            {payAtPayment?.PayAtAccountNumber && (
              <Card
                title={
                  <span style={{ color: "Green", whiteSpace: "normal" }}>
                    Second payment method - Pay@
                  </span>
                }
                styles={{ header: { whiteSpace: "normal" } }}
                style={{ marginBottom: 16 }}
              >
                <p style={{ marginBottom: 0, wordBreak: "break-all" }}>
                  Pay@ Account Number -{" "}
                  <strong>{payAtPayment.PayAtAccountNumber}</strong>
                </p>
              </Card>
            )}
          </div>
        </Col>
      </Row>
      
      <DebiCheckMaintenanceModal
        open={maintenanceModalVisible}
        onClose={() => setMaintenanceModalVisible(false)}
        memberId={memberId}
        currentPaymentDetails={card[0]}
        onSuccess={() => {
          // Refresh mandate and keep left form in sync
          getMemberApi(String(memberId)).then((response) => {
            if (response.success) {
              const primary = response.data.PaymentType;
              const secondary = response.data.SecondaryPaymentType;
              const debiCheck =
                primary?.Type === "DebiCheck"
                  ? primary
                  : secondary?.Type === "DebiCheck"
                  ? secondary
                  : null;
              const payAt =
                primary?.Type === "PayAt"
                  ? primary
                  : secondary?.Type === "PayAt"
                  ? secondary
                  : null;

              setMember(response.data);
              setPayAtPayment(payAt || null);
              if (debiCheck) {
                const refreshed = {
                  id: 1,
                  ...debiCheck,
                };
                setCard([refreshed]);
                syncFormFromMandate(refreshed, response.data);
              }
            }
          });
        }}
        userRole={userRole}
      />
    </>
  );

  return content;
};
export default NupayPaymentInfo;
